<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="table-heading"
      class="mb-8"
    />
    <v-row>
      <v-col
        v-for="loadingIdx of 36"
        :key="`loading${loadingIdx}`"
        md="3"
        sm="3"
        cols="4"
        class="my-2 d-flex align-center justify-center"
      >
        <v-skeleton-loader
          type="avatar"
          size="60"
        />
      </v-col>
    </v-row>
  </div>

  <div v-else-if="!isLoading && date">
    <v-menu
      v-if="!isSimpleMode"
      v-model="isPickingDate"
      :close-on-content-click="false"
      transition="scale-transition"
      min-width="auto"
    >
      <template #activator="{ on, attrs }">
        <v-text-field
          :value="date"
          :prepend-inner-icon="icons.mdiCalendar"
          readonly
          class="text-base pa-0 ma-0 px-4"
          v-bind="attrs"
          v-on="on"
        />
      </template>

      <v-date-picker
        v-model="date"
        locale="ja"
        :day-format="date => new Date(date).getDate()"
        color="primary"
        no-title
        @change="isPickingDate = false"
      />
    </v-menu>

    <v-tabs
      v-model="currentTabIdx"
      show-arrows
      background-color="transparent"
      class="mb-5 elevation-0"
    >
      <v-tab
        v-for="role in roles"
        :key="`v-tab-${role[0]}`"
      >
        <v-badge
          v-if="workingUsersByRole[role[0]] && workingUsersByRole[role[0]].length > 0"
          :content="workingUsersByRole[role[0]].length"
        >
          {{ role[1] }}
        </v-badge>

        <span v-else>{{ role[1] }}</span>
      </v-tab>
    </v-tabs>

    <simple-clock-in
      :club-rounding-config="currentClub"
      class="px-4"
      @is-on="isSimpleMode = $event"
    />

    <v-checkbox
      v-model="showRetired"
      label="退店者を表示"
      dense
      hide-details
      class="px-4 mt-1"
    />

    <v-tabs-items
      v-model="currentTabIdx"
      style="background: transparent"
      touchless
      class="pt-4 px-1"
    >
      <v-tab-item
        v-for="role in roles"
        :key="`v-tab-item-${role[0]}`"
      >
        <v-row>
          <v-col
            v-for="user of usersByRole[role[0]]"
            :key="`timecard_for_user_${user.id}`"
            md="3"
            sm="3"
            cols="4"
            class="my-2"
          >
            <div class="d-flex flex-column align-center mb-4">
              <v-badge
                :color="timecardsByUserId[+user.id] ? 'success' : 'secondary'"
                overlap
                offset-x="15"
                offset-y="15"
                bordered
              >
                <v-hover v-slot="{ hover }">
                  <v-avatar
                    size="60"
                    :class="{ 'elevation-4' : hover, 'is-working': timecardsByUserId[+user.id] }"
                    class="cursor-pointer"
                    color="secondary"
                    @click="openTimecardDialog(user)"
                  >
                    <v-img
                      v-if="user.attributes.profileImage"
                      :src="user.attributes.profileImage"
                    />
                    <span
                      v-else
                      class="text-xs font-weight-bold"
                    >
                      {{ user.attributes.name.substring(0, 3) }}
                    </span>
                  </v-avatar>
                </v-hover>
              </v-badge>

              <div class="mt-2 d-flex flex-wrap justify-center align-center">
                <span>
                  {{ user.attributes.name }}
                </span>

                <transition name="fadeLeft">
                  <v-switch
                    v-if="isSimpleMode"
                    :input-value="!!timecardsByUserId[+user.id]"
                    color="success"
                    hide-details
                    inset
                    class="pa-0 ma-0 mx-auto pl-4"
                    style="animation-duration: 0.3s;"
                    @click="clockInSimply({
                      timecardId: (timecardsByUserId[+user.id] || {}).id,
                      userId: user.id
                    })"
                  />
                </transition>
              </div>

              <p
                v-if="timecardsByUserId[+user.id] && timecardsByUserId[+user.id].attributes && timecardsByUserId[+user.id].attributes.startAt"
                class="text-xs secondary--text mt-2"
              >
                {{ format(parseISO(timecardsByUserId[+user.id].attributes.startAt), 'M/d HH:mm ~') }}
              </p>
            </div>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog
      v-model="isTimecardDialogOpen"
      width="500"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex">
          <v-badge
            :color="timecardsByUserId[+targetUser.id] ? 'success' : 'secondary'"
            overlap
            offset-x="15"
            offset-y="15"
            bordered
          >
            <v-avatar color="secondary">
              <v-img
                v-if="targetUser.attributes.profileImage"
                :src="targetUser.attributes.profileImage"
              />
              <span
                v-else
                class="text-xs font-weight-bold"
              >
                {{ targetUser.attributes.name.substring(0, 3) }}
              </span>
            </v-avatar>
          </v-badge>

          <div class="ml-6">
            {{ targetUser.attributes.name }}
          </div>

          <v-chip
            v-if="startAtSourceLabel"
            small
            class="ml-3 check-in-chip"
            :class="`check-in-chip--${editingAttributes.startAtSource}`"
          >
            {{ startAtSourceLabel }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                ref="startAtInput"
                :value="currentTimecard.startAt ? hhmm(currentTimecard.startAt) : ''"
                :label="isPunchingWithRule ? '打刻時刻' : 'IN'"
                :error="!currentTimecard.startAt"
                type="time"
                :messages="currentTimecard.startAt ? format(parseISO(currentTimecard.startAt), 'M/d HH:mm') : ''"
                @change="
                  changeTime({ time: $event, key: 'startAt' });
                  currentTimecard.leaveAt = null;
                  clearAllBreakTimecards();
                "
              />
            </v-col>

            <v-col
              v-if="isPunchingWithRule"
              cols="6"
            >
              <div class="text-xs secondary--text">
                出勤時刻(自動)
              </div>
              <div class="text-xl font-weight-semibold">
                {{ calculation && calculation.startAt ? hhmm(calculation.startAt) : '—' }}
              </div>
            </v-col>

            <v-col
              v-else
              cols="6"
            >
              <v-text-field
                v-if="currentTimecard.id && currentTimecard.startAt"
                ref="leaveAtInput"
                :value="currentTimecard.leaveAt ? hhmm(currentTimecard.leaveAt) : ''"
                label="OUT"
                :error="!!currentTimecard.leaveAt && (currentTimecard.leaveAt < currentTimecard.startAt)"
                type="time"
                :messages="currentTimecard.leaveAt ? format(parseISO(currentTimecard.leaveAt), 'M/d HH:mm') : ''"
                @change="changeTime({ time: $event, key: 'leaveAt', startAt: currentTimecard.startAt })"
              />
            </v-col>
          </v-row>

          <template v-if="!isAlliance && currentTimecard.id && currentTimecard.startAt">
            <v-row
              v-for="(breakTimecard, breakTimecardIdx) in currentTimecard.breakTimecardsAttributes"
              :key="`breakTimecards[${breakTimecardIdx}]`"
            >
              <v-col
                cols="12"
                class="mt-4 pb-0 text-base"
              >
                休憩
              </v-col>

              <v-col cols="6">
                <v-text-field
                  ref="breakTimeStartAtInput"
                  :value="breakTimecard.breakTimeStartAt ? hhmm(breakTimecard.breakTimeStartAt) : ''"
                  label="IN"
                  type="time"
                  :messages="breakTimecard.breakTimeStartAt ? format(parseISO(breakTimecard.breakTimeStartAt), 'M/d HH:mm') : ''"
                  @change="changeTime({
                    time: $event,
                    key: `breakTimecardsAttributes[${breakTimecardIdx}].breakTimeStartAt`,
                    startAt: currentTimecard.startAt,
                  })"
                />
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-if="breakTimecard.breakTimeStartAt"
                  ref="breakTimeEndAtInput"
                  :value="breakTimecard.breakTimeEndAt ? hhmm(breakTimecard.breakTimeEndAt) : ''"
                  label="OUT"
                  type="time"
                  :error="!!breakTimecard.breakTimeEndAt && (breakTimecard.breakTimeEndAt < breakTimecard.breakTimeStartAt)"
                  :messages="breakTimecard.breakTimeEndAt ? format(parseISO(breakTimecard.breakTimeEndAt), 'M/d HH:mm') : ''"
                  @change="changeTime({
                    time: $event,
                    key: `breakTimecardsAttributes[${breakTimecardIdx}].breakTimeEndAt`,
                    startAt: breakTimecard.breakTimeStartAt,
                  })"
                />
              </v-col>
            </v-row>
          </template>

          <template v-if="showsDeclarations">
            <check-in-declarations
              v-model="declarations"
              :items="declarationItems"
              :declared="editingAttributes.declarations || []"
              :declared-today-item-ids="declaredTodayItemIds"
              with-amount
              class="mt-4"
            />

            <v-alert
              v-if="declarationNotice"
              dense
              text
              :type="declarationNotice.type"
              class="mt-3 mb-0 text-sm"
            >
              {{ declarationNotice.text }}
              <div
                v-if="declarationNotice.canReapply"
                class="mt-2"
              >
                <v-btn
                  small
                  outlined
                  color="primary"
                  :ripple="false"
                  @click="reapplyCheckInRule"
                >
                  自動の時刻({{ hhmm(calculation.startAt) }})に戻す
                </v-btn>
              </div>
            </v-alert>

            <p
              v-if="editingAttributes.punchedAt"
              class="text-xs secondary--text mt-3 mb-0"
            >
              打刻した時刻 {{ hhmm(editingAttributes.punchedAt) }}
            </p>
          </template>
        </v-card-text>

        <v-card-actions class="mt-4">
          <v-spacer />
          <v-btn
            color="primary"
            class="me-3"
            type="submit"
            :disabled="hasError"
            :ripple="false"
            @click.prevent="createOrUpdateTimecard()"
          >
            {{ currentTimecard.id ? '更新' : (showsDeclarations || isPunchingWithRule ? '出勤' : '作成') }}
          </v-btn>

          <v-btn
            text
            color="secondary"
            :ripple="false"
            @click="cancelForm"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>

  <div v-else-if="!isLoading && !date">
    営業日の取得に失敗しました。<br />
    リロードしてください。
  </div>
</template>

<script>
import {
  ref,
  getCurrentInstance,
  computed,
  inject,
  watch,
} from '@vue/composition-api'
import {
  mdiClockOutline,
  mdiTilde,
  mdiCalendar,
} from '@mdi/js'
import {
  chain,
  findIndex,
  groupBy,
  isEmpty,
  each,
  update,
} from 'lodash'
import {
  addDays,
  parseISO,
  format,
  formatISO,
} from 'date-fns'
import UserApi from '@/api/waiter/User'
import TimecardApi from '@/api/waiter/Timecard'
import TimeApi from '@/api/v2/utility/Time'
import useValidation from '@/views/composable/useValidation'
import useTimecard from '@/views/composable/useTimecard'
import useCompInit from '@/views/composable/useCompInit'
import useDateFormat from '@/views/composable/useDateFormat'
import useCurrentData from '@/views/composable/useCurrentData'
import useClubBusinessHours from '@/views/composable/useClubBusinessHours'
import useCheckInSettings from '@/views/composable/useCheckInSettings'
import SimpleClockIn from '@/views/components/timecard/SimpleClockIn.vue'
import CheckInDeclarations from '@/views/components/timecard/CheckInDeclarations.vue'

export default {
  components: {
    SimpleClockIn,
    CheckInDeclarations,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const getClub = inject('getClub', () => {})
    const fetchPlusMinusStats = inject('fetchPlusMinusStats', () => {})
    const fetchOperationRateStats = inject('fetchOperationRateStats', () => {})
    const date = ref(currentClubMeta.today)
    const isPickingDate = ref(false)
    const isSimpleMode = ref(false)
    const startAtInput = ref()
    const leaveAtInput = ref()
    const breakTimeStartAtInput = ref()
    const breakTimeEndAtInput = ref()
    const users = ref([])
    const targetUser = ref({
      attributes: {
        name: '',
        id: 0,
      },
    })
    const timecards = ref([])
    const currentTabIdx = ref(0)
    const currentTimecard = ref({})
    const isTimecardDialogOpen = ref(false)
    const tmpStartAt = ref()

    const { isValidId } = useValidation()
    const { buildTimecardForm, buildTimecardEditForm, simpleCurrentTime } = useTimecard()
    const { isLoading, initWith } = useCompInit()
    const { hhmm } = useDateFormat()
    const { currentClub } = useCurrentData()

    // 出勤時の申告と出勤時刻の自動決定。出勤時刻の計算式は画面に持たず、サーバーに問い合わせる
    const {
      declarationItems,
      hasDeclarationItems,
      isTimeRuleEnabled,
      getCheckInSettings,
      getDeclaredTodayItemIds,
      calculateStartAt,
    } = useCheckInSettings()
    const declarations = ref([])
    const declaredTodayItemIds = ref([])
    const calculation = ref(null)
    const editingTimecardData = ref(null)

    // 表示中の営業日(date)当時の開店/閉店時刻(HH:mm)。サーバの営業時間履歴を時点解決した値を優先し、
    // 未取得・取得失敗時は現在値へフォールバック(過去営業日の勤怠編集で変更前の営業時間が正しく使われる)
    const { businessHours } = useClubBusinessHours(date)
    const resolvedOpenHHmm = () => businessHours.value?.open_at || hhmm(currentClub.value.openAt)
    const resolvedCloseHHmm = () => businessHours.value?.close_at || hhmm(currentClub.value.closeAt)

    const roles = [
      ['cast', 'キャスト'],
      ['waiter', 'スタッフ'],
      ['alliance', 'アライアンス'],
    ]
    const isAlliance = computed(() => roles[currentTabIdx.value][0] === 'alliance')

    const hasError = computed(() => {
      return startAtInput.value?.hasError
        || leaveAtInput.value?.hasError
        || breakTimeStartAtInput.value?.hasError
        || breakTimeEndAtInput.value?.hasError
    })

    const workingUserIds = computed(() => {
      return chain(timecards.value)
        .filter(timecard => isEmpty(timecard.attributes.leaveAt))
        .map(timecard => Number(timecard.attributes.userId))
        .value()
    })

    // 退店者(retiredAt)は既定で非表示。「退店者を表示」チェックで表示する
    const showRetired = ref(false)
    const visibleUsers = computed(() => users.value.filter(user => showRetired.value || !user.attributes.retiredAt))

    const workingUsersByRole = computed(() => {
      return chain(visibleUsers.value)
        .filter(user => workingUserIds.value.includes(+user.id))
        .groupBy('attributes.role')
        .value()
    })

    const timecardsByUserId = computed(() => {
      const obj = {}

      each(timecards.value, o => {
        if (o.attributes.leaveAt) return

        obj[+o.attributes.userId] = o
      })

      return obj
    })

    const usersByRole = computed(() => {
      return groupBy(visibleUsers.value, 'attributes.role')
    })

    const cancelForm = () => {
      isTimecardDialogOpen.value = false
      currentTimecard.value = {}
      tmpStartAt.value = null
      declarations.value = []
      declaredTodayItemIds.value = []
      calculation.value = null
      editingTimecardData.value = null
    }

    const editingAttributes = computed(() => editingTimecardData.value?.attributes || {})
    const isCreating = computed(() => !isValidId(currentTimecard.value.id))

    // 出勤させるとき、出勤時刻をサーバーが決める店舗か
    const isPunchingWithRule = computed(() => isCreating.value && isTimeRuleEnabled.value && !isAlliance.value)

    const showsDeclarations = computed(() => {
      if (isAlliance.value) return false

      return hasDeclarationItems.value || (editingAttributes.value.declarations || []).length > 0
    })

    const startAtSourceLabel = computed(() => {
      return { rule: '出勤時刻は自動', manual: '出勤時刻は手修正' }[editingAttributes.value.startAtSource] || null
    })

    const declarationItemIds = computed(() => declarations.value.map(declaration => +declaration.itemId))

    const isSameTime = (a, b) => !!a && !!b && parseISO(a).getTime() === parseISO(b).getTime()

    // 申告を変えたときに、出勤時刻がどうなるかの案内
    const declarationNotice = computed(() => {
      if (isCreating.value || !calculation.value?.startAt) return null

      if (!calculation.value.recalculates) {
        if (editingAttributes.value.startAtSource !== 'manual') return null

        return {
          type: 'warning',
          text: '出勤時刻は手で直してあるため、申告を変えても変わりません。',
          canReapply: true,
        }
      }

      if (isSameTime(calculation.value.startAt, calculation.value.currentStartAt)) return null

      return {
        type: 'info',
        text: `更新すると、出勤時刻が ${hhmm(calculation.value.currentStartAt)} から ${hhmm(calculation.value.startAt)} に変わります。`,
        canReapply: false,
      }
    })

    const refreshCalculation = async () => {
      if (!isTimecardDialogOpen.value) return

      if (isCreating.value) {
        calculation.value = isPunchingWithRule.value && currentTimecard.value.startAt
          ? await calculateStartAt({ punchedAt: currentTimecard.value.startAt, declarationItemIds: declarationItemIds.value })
          : null

        return
      }

      calculation.value = editingAttributes.value.punchedAt
        ? await calculateStartAt({ timecardId: currentTimecard.value.id, declarationItemIds: declarationItemIds.value })
        : null
    }

    watch([declarationItemIds, () => currentTimecard.value.startAt], refreshCalculation)

    const replaceTimecard = timecard => {
      const timecardIdx = findIndex(timecards.value, o => Number(o.id) === Number(timecard.id))

      timecards.value.splice(timecardIdx, 1, timecard)
    }

    const getUsers = async () => {
      const res = await UserApi.getUsers()

      if (res?.data) {
        // changed to array
        users.value = [...res.data.users.data]
      }
    }

    const getToday = async () => {
      const res = await TimeApi.getToday()

      if (res.data.status === 'success') {
        date.value = res.data.data
      }
    }

    // 出勤時刻ルールか申告がある店舗の出勤は、打刻として送る(出勤時刻はサーバーが決める)
    const requestCreateTimecard = () => {
      if (isAlliance.value || !(isTimeRuleEnabled.value || hasDeclarationItems.value)) {
        return TimecardApi.createTimecard(currentTimecard.value)
      }

      return TimecardApi.punchTimecard({
        userId: currentTimecard.value.userId,
        punchedAt: currentTimecard.value.punchedAt || currentTimecard.value.startAt,
        startAt: currentTimecard.value.startAt,
        declarationItemIds: declarationItemIds.value,
        declarations: declarations.value.map(declaration => ({ itemId: +declaration.itemId, amount: declaration.amount })),
      })
    }

    const createTimecard = async () => {
      try {
        const res = await requestCreateTimecard()
        if (res?.data) {
          timecards.value.push(res.data.timecard.data)
          vm.$toast.success(`${targetUser.value.attributes.name}を${isAlliance.value ? '待機' : '出勤'}中にしました`)
        }
      } catch (error) {
        if (error?.response?.status === 422) {
          vm.$toast.error('既に出勤中のタイムカードがあります')
          const refreshRes = await TimecardApi.getTimecards({ isWorking: true })
          if (refreshRes?.data) {
            timecards.value = [...refreshRes.data.timecards.data]
          }
        } else {
          vm.$toast.error('タイムカードの作成に失敗しました')
        }
      }

      cancelForm()
      Promise.all([fetchPlusMinusStats(), fetchOperationRateStats()])
    }

    // withDeclarations: ダイアログからの更新で、申告の変更を一緒に送るか
    const updateTimecard = async ({ withDeclarations = false } = {}) => {
      let res
      try {
        res = withDeclarations
          ? await TimecardApi.updateTimecardWithDeclarations(currentTimecard.value, declarations.value)
          : await TimecardApi.updateTimecard(currentTimecard.value)
      } catch (error) {
        const messages = error?.response?.data?.message
        vm.$toast.error([].concat(messages || 'タイムカードの更新に失敗しました').join(' '))

        return
      }

      if (res?.data) {
        replaceTimecard(res.data.timecard.data)

        vm.$toast.success(`${targetUser.value.attributes.name}の${isAlliance.value ? '待機中を解除しました' : '勤務状態を更新しました'}`)
      }

      cancelForm()
      Promise.all([fetchPlusMinusStats(), fetchOperationRateStats()])
    }

    const getTimecards = async () => {
      const res = await TimecardApi.getTimecards({ isWorking: true })

      if (res?.data) {
        timecards.value = [...res.data.timecards.data]
      }
    }

    const reapplyCheckInRule = async () => {
      try {
        const res = await TimecardApi.reapplyCheckInRule(currentTimecard.value.id)
        if (res?.data) {
          replaceTimecard(res.data.timecard.data)
          vm.$toast.success(`${targetUser.value.attributes.name}の出勤時刻を自動の時刻に戻しました`)
        }
      } catch (error) {
        const messages = error?.response?.data?.message
        vm.$toast.error([].concat(messages || '出勤時刻を戻せませんでした').join(' '))

        return
      }

      cancelForm()
    }

    const createOrUpdateTimecard = () => {
      if (isValidId(currentTimecard.value.id)) {
        updateTimecard({ withDeclarations: showsDeclarations.value })
      } else {
        createTimecard()
      }
    }

    // 営業時間が0時を跨ぐ場合の日付計算
    const _calcBusinessTimeCrossingMidnight = (time, startAt = null) => {
      const openAtTimeOnly = resolvedOpenHHmm()

      let baseDate
      if (startAt) {
        // startAtの日付をそのまま使用（日付は変わらない）
        const startAtDate = parseISO(startAt)
        baseDate = format(startAtDate, 'yyyy-MM-dd')
      } else {
        baseDate = date.value
      }

      let newDatetime = parseISO(`${baseDate} ${time}`)
      const midnightDatetime = parseISO(`${baseDate} ${openAtTimeOnly}`)

      const isMidnightBeforeNewDatetime = midnightDatetime > newDatetime
      const isStartAtBeforeNewDatetime = startAt && parseISO(startAt) > newDatetime

      let needsDateAdjustment
      if (startAt) {
        // startAtがある場合、newDatetimeがstartAtより前で、かつ営業開始時刻より前の場合のみ日付を調整
        // ただし、startAtが0時台の場合は日付を調整しない（startAtの日付を維持）
        const startAtTimeOnly = format(parseISO(startAt), 'HH:mm')
        const isStartAtInEarlyMorning = startAtTimeOnly < openAtTimeOnly

        if (isStartAtInEarlyMorning) {
          // startAtが0時台の場合、日付は変わらない
          needsDateAdjustment = false
        } else {
          // startAtが営業開始時刻以降の場合、通常のロジック
          needsDateAdjustment = isMidnightBeforeNewDatetime || isStartAtBeforeNewDatetime
        }
      } else {
        // startAtがない場合
        needsDateAdjustment = isMidnightBeforeNewDatetime
      }

      if (needsDateAdjustment) {
        newDatetime = addDays(newDatetime, 1)
      }

      return newDatetime
    }

    // 営業時間が0時を跨がない場合の日付計算
    const _calcBusinessTimeNotCrossingMidnight = (time, startAt = null) => {
      let baseDate
      if (startAt) {
        // startAtの日付をそのまま使用（日付は変わらない）
        const startAtDate = parseISO(startAt)
        baseDate = format(startAtDate, 'yyyy-MM-dd')
      } else {
        baseDate = date.value
      }

      let newDatetime = parseISO(`${baseDate} ${time}`)
      const midnightDatetime = parseISO(`${baseDate} ${resolvedOpenHHmm()}`)

      const isMidnightBeforeNewDatetime = midnightDatetime > newDatetime
      const isStartAtBeforeNewDatetime = startAt && parseISO(startAt) > newDatetime
      const needsDateAdjustment = (startAt && (isMidnightBeforeNewDatetime || isStartAtBeforeNewDatetime)) || (!startAt && isMidnightBeforeNewDatetime)

      if (needsDateAdjustment) {
        newDatetime = addDays(newDatetime, 1)
      }

      return newDatetime
    }

    // 営業時間が0時を跨ぐかどうかを判定して適切な関数を呼び出す
    const calcBusinessTime = (time, startAt = null) => {
      const openAtTimeOnly = resolvedOpenHHmm()
      const closeAtTimeOnly = resolvedCloseHHmm()
      const isCrossingMidnight = openAtTimeOnly > closeAtTimeOnly

      if (isCrossingMidnight) {
        return _calcBusinessTimeCrossingMidnight(time, startAt)
      }

      return _calcBusinessTimeNotCrossingMidnight(time, startAt)
    }

    const changeTime = ({ time, key, startAt = null }) => {
      const newDatetime = calcBusinessTime(time, startAt)
      update(currentTimecard.value, key, () => formatISO(newDatetime))
    }

    // 「1営業日に1回まで」の項目で、同じ営業日のほかのタイムカードですでに申告したもの
    const loadDeclaredToday = async timecardData => {
      declaredTodayItemIds.value = []
      if (!hasDeclarationItems.value || isAlliance.value) return

      declaredTodayItemIds.value = await getDeclaredTodayItemIds({
        userId: targetUser.value.id,
        date: date.value,
        exceptTimecardId: timecardData?.id || null,
      })
    }

    const openTimecardDialog = user => {
      targetUser.value = user
      const timecardData = timecardsByUserId.value[+targetUser.value.id]

      currentTimecard.value = timecardData
        ? buildTimecardEditForm(timecardData)
        : buildTimecardForm({
          userId: targetUser.value.id,
          startAt: formatISO(calcBusinessTime(hhmm(new Date()))),
        })

      if (currentTimecard.value?.startAt) {
        tmpStartAt.value = currentTimecard.value?.startAt
      }

      editingTimecardData.value = timecardData || null
      declarations.value = (timecardData?.attributes?.declarations || [])
        .map(declaration => ({ itemId: declaration.itemId, amount: declaration.amount }))

      isTimecardDialogOpen.value = true
      refreshCalculation()
      loadDeclaredToday(timecardData)
    }

    const clockInSimply = ({ timecardId = null, userId }) => {
      const now = simpleCurrentTime(currentClub.value)
      targetUser.value = users.value.find(user => +user.id === +userId)

      if (timecardId) {
        const timecardData = timecardsByUserId.value[+userId]
        if (!timecardData) return
        currentTimecard.value = {
          ...buildTimecardEditForm(timecardData),
          leaveAt: now,
        }

        changeTime({ time: hhmm(now), key: 'leaveAt', startAt: currentTimecard.value.startAt })

        updateTimecard()
      } else if (hasDeclarationItems.value && !isAlliance.value) {
        // 申告項目がある店舗は、申告を選んでから出勤にする
        openTimecardDialog(targetUser.value)
      } else {
        currentTimecard.value = {
          ...buildTimecardForm({ userId, startAt: now }),

          // 出勤時刻ルールがある店舗では、丸める前の時刻を打刻時刻として送る
          punchedAt: formatISO(new Date()),
        }

        createTimecard()
      }
    }

    const clearAllBreakTimecards = () => {
      if (currentTimecard.value && currentTimecard.value.breakTimecardsAttributes) {
        each(currentTimecard.value.breakTimecardsAttributes, breakTimecard => {
          update(breakTimecard, 'breakTimeStartAt', () => null)
          update(breakTimecard, 'breakTimeEndAt', () => null)
        })
      }
    }

    initWith([
      getClub(),
      getUsers(),
      getTimecards(),
      getToday(),
      getCheckInSettings(),
    ])

    return {
      // data
      startAtInput,
      leaveAtInput,
      breakTimeStartAtInput,
      breakTimeEndAtInput,
      isLoading,
      currentTabIdx,
      users,
      isTimecardDialogOpen,
      targetUser,
      currentTimecard,
      timecards,
      workingUserIds,
      tmpStartAt,
      roles,
      date,
      currentClubMeta,
      isPickingDate,
      isSimpleMode,
      showRetired,
      declarations,
      declarationItems,
      declaredTodayItemIds,
      calculation,

      // computed
      usersByRole,
      timecardsByUserId,
      isAlliance,
      hasError,
      workingUsersByRole,
      editingAttributes,
      isPunchingWithRule,
      showsDeclarations,
      startAtSourceLabel,
      declarationNotice,

      // methods
      openTimecardDialog,
      createTimecard,
      updateTimecard,
      createOrUpdateTimecard,
      cancelForm,
      hhmm,
      changeTime,
      clockInSimply,
      reapplyCheckInRule,
      parseISO,
      format,
      clearAllBreakTimecards,

      icons: {
        mdiClockOutline,
        mdiTilde,
        mdiCalendar,
      },
    }
  },
}
</script>

<style scoped>
@keyframes float {
  0% {
    box-shadow: 0 5px 15px 0px rgba(0,0,0,0.4);
    transform: translatey(0px);
  }
  50% {
    box-shadow: 0 25px 15px 0px rgba(0,0,0,0.1);
    transform: translatey(-3px);
  }
  100% {
    box-shadow: 0 5px 15px 0px rgba(0,0,0,0.4);
    transform: translatey(0px);
  }
}
.is-working {
  transform: translatey(0px);
  animation: float 6s ease-in-out infinite;
}

/* Vuetify の color pack は無効のため、色は独自クラスで指定する */
.check-in-chip--rule {
  background: rgba(86, 202, 0, 0.12) !important;
  color: #3c8c00 !important;
}
.check-in-chip--manual {
  background: rgba(22, 177, 255, 0.12) !important;
  color: #0a6e9f !important;
}
</style>
